import { Trans } from '@lingui/react'
import { arrayOf, bool, func, shape, string } from 'prop-types'

import PreQuerySuggestionsExtended from 'shared/experiments/components/PreQuerySuggestionsExtended'
import {
  getExperimentNamePerTenant,
  preQuerySuggestionsExperimentEnabledAndNotDefault,
} from 'shared/experiments/components/PreQuerySuggestionsExtended/helpers'
import {
  EXPERIMENT_VARIATION,
  SWITCH_SEARCH_PLACEHOLDER_TEXT,
} from 'shared/experiments/consts'
import { getExperiment } from 'shared/experiments/utils/experiments'
import { isRebrandingITActive } from 'shared/experiments/utils/rebranding'
import { withI18n } from 'shared/utils/withI18n'
import Form from 'views/components/atoms/Form'
import IconArrowLeft from 'views/components/atoms/Icon/IconArrowLeft'
import IconCaretLeft from 'views/components/atoms/Icon/IconCaretLeft'
import ButtonTransparent from 'views/components/molecules/ButtonTransparent'
import InputTextWithFloatingLabel from 'views/components/molecules/InputTextWithFloatingLabel'
import PreQuerySuggestions from 'views/components/organisms/PreQuerySuggestions'
import { EXCLUDED_PAGE_TYPES } from 'views/components/organisms/PreQuerySuggestions/consts'
import { shouldRenderPqsl } from 'views/components/organisms/PreQuerySuggestions/helpers'
import { getPlaceholderTextForExperiment } from 'views/components/organisms/SearchBox/helper'
import SearchBoxList from 'views/components/organisms/SearchBox/SearchBoxList'
import SearchHistory from 'views/components/organisms/SearchBox/SearchHistory'
import Experiment from 'views/components/utils/optimizely/Experiment'
import { publicRuntimeConfigType } from 'views/utils/propTypes'

import './SearchBoxMobile.scss'

const SearchBoxMobile = ({
  publicRuntimeConfig: {
    pageProperties: { pageType, experiments },
    publicConfig: { deviceClass, tenant },
  },
  i18n,
  query,
  inputId,
  displayResults,
  products,
  searchHistory,
  handleClear,
  handleShowAll,
  handleSubmit,
  handleClearHistory,
  handleItemClick,
  handleQueryUpdate,
  handleInputUpdate,
  formAction,
  topProducts,
  topSearches,
  withHighlightedProduct,
}) => {
  const inputProps = {
    inputType: 'search',
    name: 'query',
    inputId,
    disableAutoCompleteAndFriends: true,
    onChange: handleInputUpdate,
    onClear: handleClear,
    value: query,
    rounded: false,
    inputClassname: 'o-SearchBox__input',
    'data-clientside-hook': 'search__input--mobile',
  }

  const searchBoxListProps = {
    mobile: true,
    query,
    displayResults,
    products,
    handleShowAll,
    handleItemClick,
    formAction,
    withHighlightedProduct,
  }

  const preQuerySuggestionsProps = {
    searchHistory,
    handleClearHistory,
    handleQueryUpdate,
    fromPreQuerySuggestions: true,
  }

  const isRebrandingEnabled = isRebrandingITActive(experiments)

  const switchSearchPlaceholderExperiment = getExperiment(
    SWITCH_SEARCH_PLACEHOLDER_TEXT,
    experiments
  )
  const defaultPlaceHolderText = i18n._('general.label.placeholder.search')
  const getPlaceholderTextToRender = () => {
    if (switchSearchPlaceholderExperiment.isEnabled) {
      return getPlaceholderTextForExperiment(
        switchSearchPlaceholderExperiment.variant,
        defaultPlaceHolderText,
        i18n
      )
    } else {
      return defaultPlaceHolderText
    }
  }
  return (
    <div className="o-SearchBox">
      <div className="l-flex o-Searchbox__header">
        <div>
          <ButtonTransparent
            href="#"
            name="smallheader-hide-search-button"
            data-clientside-hook="hideSearchboxMobile"
          >
            {isRebrandingEnabled ? <IconCaretLeft /> : <IconArrowLeft />}
            <span className="a-visuallyhidden">
              <Trans id="general.link.label.back" />
            </span>
          </ButtonTransparent>
        </div>
        <Form
          className="l-flex__primary"
          name="search-box"
          method="get"
          data-clientside-hook="chargeUrlSearchParams"
          action={formAction}
          onSubmit={handleSubmit}
        >
          <InputTextWithFloatingLabel {...inputProps}>
            <div>{getPlaceholderTextToRender()}</div>
          </InputTextWithFloatingLabel>
        </Form>
      </div>

      <SearchBoxList {...searchBoxListProps} />
      {!query &&
        shouldRenderPqsl({
          pageType,
          tenant,
          preQuerySuggestionsExperimentEnabled:
            preQuerySuggestionsExperimentEnabledAndNotDefault(
              tenant,
              deviceClass,
              experiments
            ),
        }) && (
          <PreQuerySuggestions
            mobile
            hasSearchHistory={Boolean(searchHistory?.length)}
            topSearches={topSearches}
            topProducts={topProducts}
          >
            <SearchHistory {...preQuerySuggestionsProps} />
          </PreQuerySuggestions>
        )}
      {!query && !EXCLUDED_PAGE_TYPES.includes(pageType) && (
        <Experiment
          classNames="u-relative"
          name={getExperimentNamePerTenant(tenant, deviceClass)}
        >
          {({ isEnabled, variant }) => {
            if (isEnabled && variant !== EXPERIMENT_VARIATION.DEFAULT) {
              return (
                <PreQuerySuggestionsExtended
                  mobile
                  hasSearchHistory={Boolean(searchHistory?.length)}
                  topBrands={variant === EXPERIMENT_VARIATION.V3}
                  topCategories={variant === EXPERIMENT_VARIATION.V2}
                >
                  <SearchHistory {...preQuerySuggestionsProps} />
                </PreQuerySuggestionsExtended>
              )
            }
          }}
        </Experiment>
      )}
    </div>
  )
}

SearchBoxMobile.propTypes = {
  ...SearchHistory.propTypes,
  ...SearchBoxList.propTypes,
  // comes from SearchBoxContainer
  publicRuntimeConfig: shape(publicRuntimeConfigType).isRequired,
  inputId: string.isRequired,
  formAction: string.isRequired,
  handleChange: func,
  handleClear: func,
  // These two are only for the stories
  topSearches: arrayOf(shape({})),
  topProducts: arrayOf(shape({})),
  withHighlightedProduct: bool,
}

SearchBoxMobile.defaultProps = {
  ...SearchHistory.defaultProps,
  ...SearchBoxList.defaultProps,
  handleChange: null,
  handleClear: null,
  topSearches: null,
  topProducts: null,
  withHighlightedProduct: false,
}

export default withI18n()(SearchBoxMobile)
